import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import {
  certificateReplacementAtom,
  CertificateReplacementState,
  certificateReplacementStateSelector,
  certificateToReplaceIdAtom,
  isCertificateToReplaceOperationAtom,
  isUploadValidExtendedByAtom,
} from "../state/uploadCertificateStateBase";

/**
 * Custom hook that provides a unified interface for managing certificate replacement state.
 * This hook consolidates the logic for both the old separate atoms and the new combined approach.
 */
export const useCertificateReplacement = () => {
  // New combined approach
  const replacementState = useRecoilValue(certificateReplacementStateSelector);
  const setCertificateReplacement = useSetRecoilState(certificateReplacementAtom);

  // Legacy atoms for backward compatibility
  const setCertificateToReplaceId = useSetRecoilState(certificateToReplaceIdAtom);
  const setIsUploadValidExtendedBy = useSetRecoilState(isUploadValidExtendedByAtom);
  const resetCertificateToReplaceId = useResetRecoilState(certificateToReplaceIdAtom);
  const resetIsUploadValidExtendedBy = useResetRecoilState(isUploadValidExtendedByAtom);
  const resetCertificateReplacement = useResetRecoilState(certificateReplacementAtom);
  const setIsCertificateToReplaceOperation = useSetRecoilState(isCertificateToReplaceOperationAtom);
  const resetIsCertificateToReplaceOperation = useResetRecoilState(isCertificateToReplaceOperationAtom);

  /**
   * Set certificate replacement state using the unified interface
   */
  const setReplacement = (state: CertificateReplacementState) => {
    // Set both old and new atoms for compatibility during transition
    if (state) {
      setCertificateToReplaceId(state.certificateId);
      setIsUploadValidExtendedBy(state.operation === "extend");
      setIsCertificateToReplaceOperation(state.operation === "replace");
      setCertificateReplacement(state);
    } else {
      resetCertificateToReplaceId();
      resetIsUploadValidExtendedBy();
      resetIsCertificateToReplaceOperation();
      resetCertificateReplacement();
    }
  };

  /**
   * Reset all certificate replacement state
   */
  const resetReplacement = () => {
    resetIsCertificateToReplaceOperation();
    resetCertificateToReplaceId();
    resetIsUploadValidExtendedBy();
    resetCertificateReplacement();
  };

  /**
   * Check if there is an active replacement operation
   */
  const hasReplacement = !!replacementState;

  /**
   * Check if the current operation is an extension
   */
  const isExtension = replacementState?.operation === "extend";

  /**
   * Check if the current operation is a standard replacement
   */
  const isReplacement = replacementState?.operation === "replace";

  /**
   * Get the certificate ID being replaced
   */
  const certificateId = replacementState?.certificateId;

  return {
    // Current state
    replacementState,
    certificateId,
    hasReplacement,
    isExtension,
    isReplacement,

    // Actions
    setReplacement,
    resetReplacement,
  };
};

export default useCertificateReplacement;
